import {
  arrayRemove,
  arrayUnion,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { useCallback, useEffect } from "react";
import { db } from "../firebaseConfig";
import { useAppDispatch } from "../store/hooks";
import {
  LiveSessionQuestion,
  LiveSessionStudentAnswer,
  setLiveSessionState,
} from "../store/slices/liveSessionSlice";
import { getDeviceId, isTeacher } from "./utils";

const useLiveSessionFirestore = (
  sessionId: string,
  isPurchased?: boolean,
  serverTimestamp?: number
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    try {
      if (!sessionId || (!isPurchased && !isTeacher())) {
        console.error("Invalid sessionId provided to useLiveSessionFirestore or user cannot access this session.");
        return;
      }
      const sessionDocRef = doc(db, "live_sessions", sessionId);

      const unsubscribe = onSnapshot(sessionDocRef, (doc) => {
        console.log("vdshresgs", doc.data());
        if (doc.exists()) {
          const studentsNames = {};
          doc.data()?.studentsNames?.forEach((name: string) => {
            const [id, studentName] = name.split("=>");
            studentsNames[id] = studentName;
          });

          dispatch(setLiveSessionState({ ...doc.data(), studentsNames }));
        } else {
          console.log("No such document!");
        }
      });

      return () => unsubscribe();
    } catch (e) {
      console.error("Error fetching live session data:", e);
    }
  }, [sessionId, dispatch, isPurchased]);

  const addStudent = useCallback(
    async (studentId: number, studentName: string) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        students: arrayUnion(studentId),
        studentsNames: arrayUnion(studentId + "=>" + studentName),
        studentsDevices: arrayUnion({
          studentId,
          deviceId: getDeviceId(),
          serverTimestamp,
        }),
      });
    },
    [sessionId]
  );

  const removeStudent = useCallback(
    async (studentId: number) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        students: arrayRemove(studentId),
        studentsDevices: arrayRemove({
          studentId,
          deviceId: getDeviceId(),
          serverTimestamp,
        }),
      });
    },
    [sessionId]
  );

  const kickoutStudent = useCallback(
    async (studentId: number) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        kickedOut: arrayUnion(studentId),
      });
    },
    [sessionId]
  );

  const raiseHand = useCallback(
    async (studentId: number) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        hands_raised: arrayUnion(studentId),
      });
    },
    [sessionId]
  );

  const lowerHand = useCallback(
    async (studentId: number) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        hands_raised: arrayRemove(studentId),
        active_mics: arrayRemove(studentId),
        allowed_to_speak: arrayRemove(studentId),
      });
    },
    [sessionId]
  );

  const allowToSpeak = useCallback(
    async (studentId: number) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        allowed_to_speak: arrayUnion(studentId),
      });
    },
    [sessionId]
  );

  const revokeSpeakingPermission = useCallback(
    async (studentId: number) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        allowed_to_speak: arrayRemove(studentId),
        hands_raised: arrayRemove(studentId),
        active_mics: arrayRemove(studentId),
      });
    },
    [sessionId]
  );

  const sendMessage = useCallback(
    async (message: { senderId: number; senderName: string; text: string }) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        chat: arrayUnion({
          ...message,
          timestamp: Date.now(),
        }),
      });
    },
    [sessionId]
  );

  const openMic = useCallback(
    async (uid: number) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        active_mics: arrayUnion(uid),
      });
    },
    [sessionId]
  );

  const closeMic = useCallback(
    async (uid: number) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        active_mics: arrayRemove(uid),
      });
    },
    [sessionId]
  );

  const updateQuestion = useCallback(
    async (payload: LiveSessionQuestion | null) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      if (!payload) {
        await updateDoc(sessionDocRef, {
          question: payload,
          studentsAnswers: [],
        });
      } else {
        await updateDoc(sessionDocRef, {
          question: payload,
        });
      }
    },
    [sessionId]
  );

  const addStudentAnswer = useCallback(
    async (payload: LiveSessionStudentAnswer) => {
      if (!sessionId) return;
      const sessionDocRef = doc(db, "live_sessions", sessionId);
      await updateDoc(sessionDocRef, {
        studentsAnswers: arrayUnion(payload),
      });
    },
    [sessionId]
  );

  return {
    addStudent,
    removeStudent,
    raiseHand,
    lowerHand,
    allowToSpeak,
    revokeSpeakingPermission,
    sendMessage,
    openMic,
    closeMic,
    kickoutStudent,
    updateQuestion,
    addStudentAnswer,
  };
};

export default useLiveSessionFirestore;
